import { useEffect, useRef, useState } from "react";
import { Boxes, Check, FileText, Minus, Plus, Search, ShoppingCart, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl, dataBR, fileUrl, openPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, Field, Surface, Thumb } from "@/components/ds";
import { ProcessingOverlay } from "@/components/ds/ProcessingOverlay";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { DeliveryEstimateCard, useDeliveryEstimate } from "@/components/ds/DeliveryEstimateCard";
import { ItemFulfillmentLine, fulfillmentText } from "@/components/partner/FulfillmentModal";

/** Busca sem acento e sem diferenciar maiúsculas. */
const norm = (v) =>
  (v || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const STATUS_LABEL = {
  SOLICITADA: "Solicitação recebida",
  APROVADA: "Aprovada",
  EM_PRODUCAO: "Em produção",
  PRODUCAO_CONCLUIDA: "Produção concluída",
  SEPARADA: "Embalando",
  ENVIADA: "Pronta para envio / retirada",
  RECEBIDA: "Concluída",
  CANCELADA: "Cancelada",
};

const STEPS = [
  { key: "SOLICITADA", label: "Solicitação recebida" },
  { key: "EM_PRODUCAO", label: "Produção" },
  { key: "PRODUCAO_CONCLUIDA", label: "Produção concluída" },
  { key: "SEPARADA", label: "Embalando" },
  { key: "ENVIADA", label: "Pronta" },
  { key: "RECEBIDA", label: "Concluída" },
];

/** Linha do tempo simples: o parceiro vê só o andamento, nunca custos internos. */
export const ReplenishTimeline = ({ status }) => {
  const done = STEPS.findIndex((s) => s.key === status);
  if (status === "CANCELADA") return null;
  return (
    <ol className="mt-3 space-y-1" data-testid="replenish-timeline">
      {STEPS.map((s, i) => (
        <li
          key={s.key}
          className={`flex items-center gap-2 text-xs ${
            i <= done ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          <span
            className={`inline-flex h-4 w-4 items-center justify-center rounded-full border text-[9px] ${
              i < done
                ? "border-emerald-500/50 bg-emerald-500/20 text-emerald-300"
                : i === done
                ? "border-primary/50 bg-primary/20 text-brand"
                : "border-white/15"
            }`}
          >
            {i <= done ? "✓" : ""}
          </span>
          {s.label}
        </li>
      ))}
    </ol>
  );
};

/** Catálogo de reposição do parceiro: lista + carrinho + pedido consignado. */
export function PartnerReplenish({ catalog, requests, lock = {}, onReload }) {
  const [cart, setCart] = useState({});
  const [picked, setPicked] = useState({});
  const [notes, setNotes] = useState("");
  const [priceType, setPriceType] = useState("CONSIGNADO");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [preview, setPreview] = useState(null);
  const [search, setSearch] = useState("");
  const [proc, setProc] = useState({ active: false, done: false, error: "", data: null });
  // Carrinho guardado no backend, por parceiro autenticado (nunca por id vindo do frontend).
  const [cartState, setCartState] = useState("loading");
  const loaded = useRef(false);
  const saveSeq = useRef(0);

  useEffect(() => {
    let alive = true;
    api
      .get("/partner/cart")
      .then(({ data }) => {
        if (!alive) return;
        setCart(data.items || {});
        setPicked(data.picked || {});
        if (data.price_type) setPriceType(data.price_type);
        setCartState("saved");
        if (data.expired) toast.info("Seu carrinho anterior expirou após 15 dias.");
      })
      .catch(() => alive && setCartState("load-error"))
      .finally(() => {
        if (alive) loaded.current = true;
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!loaded.current) return;
    setCartState("saving");
    const seq = ++saveSeq.current;
    const timer = setTimeout(() => {
      api
        .put("/partner/cart", { items: cart, picked, price_type: priceType })
        .then(() => {
          // Resposta antiga não pode sobrescrever um estado mais novo.
          if (seq === saveSeq.current) setCartState("saved");
        })
        .catch(() => {
          if (seq === saveSeq.current) setCartState("error");
        });
    }, 500);
    return () => clearTimeout(timer);
  }, [cart, picked, priceType]);

  const clearCart = () => {
    if (!window.confirm("Limpar o carrinho? Todos os itens escolhidos serão removidos.")) return;
    setCart({});
    setPicked({});
  };

  const term = norm(search);
  // Só filtra o que já está liberado para este parceiro — a lista vem do catálogo dele.
  const visible = term
    ? catalog.filter((item) =>
        [
          item.name,
          item.code,
          ...(item.variations || []).flatMap((v) => [v.name, v.sku]),
        ]
          .filter(Boolean)
          .some((f) => norm(f).includes(term))
      )
    : catalog;

  const priceOf = (item) =>
    // Preço específico do parceiro vale em qualquer tipo escolhido.
    item.price_source === "ESPECIFICO"
      ? item.consign_price
      : priceType === "PUBLICO"
      ? item.public_price
      : priceType === "LOJISTA"
      ? item.reseller_price
      : item.consign_price;

  const keyOf = (item) => {
    const variations = item.variations || [];
    if (!variations.length) return item.id;
    const vid = picked[item.id];
    return vid ? `${item.id}|${vid}` : null;
  };

  // Sem preço de consignação definido, o item não entra no cálculo da reposição.
  const priceMissing = (item) => priceType === "CONSIGNADO" && item.consign_missing;

  const change = (key, delta, item) => {
    if (item && delta > 0 && priceMissing(item)) {
      toast.error("Preço de consignação não definido. Fale com a TR3ND.");
      return;
    }
    if (!key) {
      toast.error("Escolha a combinação de cores antes de adicionar.");
      return;
    }
    setCart((c) => {
      const next = Math.max(0, (c[key] || 0) + delta);
      const copy = { ...c, [key]: next };
      if (next === 0) delete copy[key];
      return copy;
    });
  };

  const lines = Object.entries(cart)
    .map(([key, quantity]) => {
      const [id, vid] = key.split("|");
      const item = catalog.find((i) => i.id === id);
      const variation = (item?.variations || []).find((v) => v.id === vid) || null;
      return { key, item, variation, quantity };
    })
    .filter((l) => l.item);
  const totalUnits = lines.reduce((s, l) => s + l.quantity, 0);
  const totalValue = lines.reduce((s, l) => s + l.quantity * (priceOf(l.item) || 0), 0);

  // Prazo automático calculado pelo backend (mesmo motor do catálogo e do link público).
  const etaPayload = {
    items: lines
      .filter((l) => l.item.kind === "PRODUTO")
      .map((l) => ({ product_id: l.item.id, variation_id: l.variation?.id || null,
                     quantity: l.quantity })),
    kits: lines
      .filter((l) => l.item.kind === "KIT")
      .map((l) => ({ product_id: l.item.id, variation_id: l.variation?.id || null,
                     quantity: l.quantity })),
  };
  const eta = useDeliveryEstimate(
    () => api.post("/partner/delivery-estimate", etaPayload).then((r) => r.data),
    JSON.stringify(etaPayload),
    lines.length > 0
  );

  const submit = async () => {
    if (lines.length === 0) {
      toast.error("Escolha ao menos um produto ou kit.");
      return;
    }
    setBusy(true);
    setProc({ active: true, done: false, error: "", data: null });
    try {
      const { data } = await api.post("/partner/replenishments", {
        items: lines
          .filter((l) => l.item.kind === "PRODUTO")
          .map((l) => ({
            product_id: l.item.id,
            variation_id: l.variation?.id || null,
            quantity: l.quantity,
          })),
        kits: lines
          .filter((l) => l.item.kind === "KIT")
          .map((l) => ({
            product_id: l.item.id,
            variation_id: l.variation?.id || null,
            quantity: l.quantity,
          })),
        notes,
        price_type: priceType,
      });
      setProc({ active: true, done: true, error: "", data });
    } catch (err) {
      setProc({ active: true, done: false, error: apiError(err), data: null });
    } finally {
      setBusy(false);
    }
  };

  const finishProcessing = () => {
    const data = proc.data;
    setProc({ active: false, done: false, error: "", data: null });
    // O backend já apagou o carrinho deste parceiro ao confirmar a reposição.
    saveSeq.current += 1;
    setCart({});
    setPicked({});
    setCartState("saved");
    setNotes("");
    setCreated(data);
    onReload();
  };

  return (
    <div className="space-y-6" data-testid="partner-replenish">
      {(lock.blocked || lock.pending) && (
        <div
          className={`rounded-xl border p-4 ${
            lock.blocked
              ? "border-red-500/40 bg-red-500/10"
              : "border-amber-500/40 bg-amber-500/10"
          }`}
          data-testid="replenish-lock-alert"
        >
          <p
            className={`text-sm font-semibold ${
              lock.blocked ? "text-red-300" : "text-amber-300"
            }`}
          >
            {lock.blocked ? "🔒 Reposições indisponíveis" : "⚠️ Fechamento pendente"}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {lock.blocked
              ? "Novas reposições estão temporariamente indisponíveis. Entre em contato com a TR3ND para mais informações."
              : lock.overdue
              ? `Seu fechamento de ${lock.period_label} está em atraso desde ${dataBR(
                  lock.regularize_until
                )}. Faça o fechamento para regularizar.`
              : `Seu fechamento ainda não foi realizado. Regularize até ${dataBR(
                  lock.regularize_until
                )}.`}
          </p>
        </div>
      )}
      {catalog.length === 0 ? (
        <EmptyState
          icon={Boxes}
          title="Nenhum produto liberado ainda"
          description="A TR3ND ainda não liberou produtos para a sua loja. Fale com a equipe."
        />
      ) : (
        <>
        <div className="relative w-full sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar produto..."
            data-testid="replenish-search-input"
            className="h-10 rounded-full pl-9"
          />
        </div>
        {visible.length === 0 ? (
          <EmptyState
            icon={Search}
            title="Nenhum produto encontrado."
            description="Tente outro nome, código ou combinação."
          />
        ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {visible.map((item) => {
            const variations = item.variations || [];
            const activeVid = picked[item.id] || "";
            const variation = variations.find((v) => v.id === activeVid) || null;
            const key = variations.length ? (activeVid ? `${item.id}|${activeVid}` : null) : item.id;
            const ready = variation ? variation.ready : item.ready;
            const inCart = variations.length
              ? variations.reduce((s, v) => s + (cart[`${item.id}|${v.id}`] || 0), 0)
              : cart[item.id] || 0;
            return (
            <Surface
              key={item.id}
              className="flex min-w-0 flex-col gap-3 overflow-hidden p-4"
              data-testid={`replenish-item-${item.id}`}
            >
              <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                onClick={() => setPreview(item.id)}
                data-testid={`replenish-photo-${item.id}`}
                className="shrink-0 rounded-lg transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-brand/60"
                title="Ver o produto de perto"
              >
                <Thumb src={fileUrl(variation?.photo_path || item.photo_path)} alt={item.name} />
              </button>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{item.name}</p>
                <p className="truncate font-num text-[11px] text-muted-foreground">
                  {(variation?.sku || item.code) ? `${variation?.sku || item.code} · ` : ""}
                  {priceMissing(item) ? "preço não definido" : `${brl(priceOf(item))} / un`}
                </p>
                <p className="font-num text-[10px] text-muted-foreground">
                  {item.price_source === "ESPECIFICO" ? (
                    <>
                      Público {brl(item.public_price)} · Seu preço{" "}
                      <span className="text-brand">{brl(item.consign_price)}</span>
                    </>
                  ) : (
                    <>
                      Público {brl(item.public_price)} · Consignado{" "}
                      {item.consign_missing ? "não definido" : brl(item.consign_price)} ·
                      Lojista {brl(item.reseller_price)}
                    </>
                  )}
                </p>
                <p className="mt-0.5 text-[11px]">
                  {item.kind === "KIT" && (
                    <span className="mr-1 rounded-full border border-primary/40 px-1.5 text-[10px] text-brand">
                      kit
                    </span>
                  )}
                  {priceMissing(item) ? (
                    <span className="text-amber-300" data-testid={`replenish-no-price-${item.id}`}>
                      Preço de consignação não definido
                    </span>
                  ) : variations.length > 0 && !variation ? (
                    <span className="text-amber-300">Escolha a combinação</span>
                  ) : (
                    <span className={ready ? "text-emerald-300" : "text-amber-300"}>
                      {ready ? "Disponível" : "Sob produção"}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-background/60 px-1.5 py-1">
                <button
                  type="button"
                  onClick={() => change(key, -1)}
                  data-testid={`replenish-dec-${item.id}`}
                  className="p-1 text-muted-foreground"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="w-7 text-center font-num text-sm" data-testid={`replenish-qty-${item.id}`}>
                  {key ? cart[key] || 0 : inCart}
                </span>
                <button
                  type="button"
                  onClick={() => change(key, 1, item)}
                  data-testid={`replenish-inc-${item.id}`}
                  className="p-1 text-brand"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              </div>
              {variations.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid={`replenish-variations-${item.id}`}>
                  {variations.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() =>
                        setPicked((p) => ({ ...p, [item.id]: p[item.id] === v.id ? "" : v.id }))
                      }
                      data-testid={`replenish-variation-${item.id}-${v.id}`}
                      className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                        v.id === activeVid
                          ? "border-brand bg-brand/15 text-brand"
                          : "border-white/15 text-muted-foreground"
                      }`}
                    >
                      {v.name}
                      {cart[`${item.id}|${v.id}`] ? ` (${cart[`${item.id}|${v.id}`]})` : ""}
                    </button>
                  ))}
                </div>
              )}
            </Surface>
          );})}
        </div>
        )}
        </>
      )}

      {lines.length > 0 && (
        <Surface className="space-y-3 p-5" data-testid="replenish-cart">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-brand" />
            <p className="font-display text-base">Minha reposição</p>
            <span
              className={`ml-auto text-[11px] ${
                cartState === "error" || cartState === "load-error"
                  ? "text-red-300"
                  : "text-muted-foreground"
              }`}
              data-testid="replenish-cart-state"
            >
              {cartState === "saving"
                ? "Salvando..."
                : cartState === "saved"
                ? "Carrinho salvo"
                : cartState === "error"
                ? "Falha ao salvar — tente alterar de novo"
                : cartState === "load-error"
                ? "Não foi possível recuperar seu carrinho"
                : ""}
            </span>
          </div>
          <ul className="space-y-1.5 text-sm">
            {lines.map((l) => (
              <li key={l.key} className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate">
                  <span className="font-num">{l.quantity}x</span> {l.item.name}
                  {l.variation && <span className="text-brand"> · {l.variation.name}</span>}
                </span>
                <span className="shrink-0 font-num text-xs text-muted-foreground">
                  {brl(l.quantity * (priceOf(l.item) || 0))}
                </span>
                <button
                  type="button"
                  onClick={() => change(l.key, -l.quantity, null)}
                  data-testid={`replenish-cart-remove-${l.key}`}
                  className="shrink-0 text-muted-foreground transition-colors hover:text-red-300"
                  aria-label="Remover item"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between border-t border-white/10 pt-3 text-sm">
            <span className="text-muted-foreground">
              {totalUnits} un no total
            </span>
            <span className="font-num" data-testid="replenish-cart-total">
              {brl(totalValue)}
            </span>
          </div>
          <DeliveryEstimateCard estimate={eta.estimate} loading={eta.loading}
                                testid="replenish-delivery-estimate" />
          <Field label="Modalidade desta reposição">
            <div className="flex flex-wrap gap-1.5" data-testid="replenish-price-type">
              {[
                ["CONSIGNADO", "Consignado"],
                ["LOJISTA", "Lojista"],
                ["PUBLICO", "Público"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setPriceType(value)}
                  data-testid={`replenish-price-type-${value.toLowerCase()}`}
                  className={`rounded-full border px-3 py-1.5 text-[11px] transition-colors ${
                    priceType === value
                      ? "border-brand bg-brand/15 text-brand"
                      : "border-white/15 text-muted-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Observação para a TR3ND">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Ex.: preciso até sexta"
              data-testid="replenish-notes-input"
            />
          </Field>
          <Button
            onClick={submit}
            disabled={busy || !!lock.blocked}
            data-testid="submit-replenishment-btn"
            className="h-11 w-full rounded-full font-semibold transition-transform active:scale-95"
          >
            {lock.blocked ? "Reposições indisponíveis" : busy ? "Enviando..." : "Solicitar reposição"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={clearCart}
            data-testid="replenish-clear-cart-btn"
            className="h-9 w-full gap-2 rounded-full text-xs"
          >
            <Trash2 className="h-3.5 w-3.5" /> Limpar carrinho
          </Button>
          <p className="text-[11px] text-muted-foreground">
            Nada muda no seu estoque até a TR3ND enviar os produtos. O preço da modalidade
            escolhida é o que vale para esta reposição.
          </p>
        </Surface>
      )}

      <div>
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Minhas solicitações
        </p>
        {requests.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma solicitação ainda.</p>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => (
              <RequestCard key={r.id} request={r} onReload={onReload} />
            ))}
          </div>
        )}
      </div>

      {created && (
        <CreatedModal request={created} onClose={() => setCreated(null)} />
      )}
      {proc.active && (
        <ProcessingOverlay
          title="Preparando sua reposição..."
          steps={[
            "Conferindo produtos e combinações",
            "Verificando disponibilidade",
            "Calculando o que precisa ser produzido",
            "Registrando sua solicitação",
          ]}
          done={proc.done}
          error={proc.error}
          successTitle="Pedido de reposição confirmado!"
          successText="Obrigado! Sua solicitação foi registrada."
          onFinish={finishProcessing}
          onRetry={() => submit()}
          onClose={() => setProc({ active: false, done: false, error: "", data: null })}
          testid="replenish-processing"
        />
      )}

      {preview && (
        <ProductPreviewModal
          item={catalog.find((i) => i.id === preview)}
          picked={picked}
          setPicked={setPicked}
          cart={cart}
          change={change}
          priceType={priceType}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

/** Só visualização: reaproveita o mesmo carrinho, preços e disponibilidade da tela. */
function ProductPreviewModal({ item, picked, setPicked, cart, change, priceType, onClose }) {
  const [shot, setShot] = useState(0);
  if (!item) return null;

  const variations = item.variations || [];
  const activeVid = picked[item.id] || "";
  const variation = variations.find((v) => v.id === activeVid) || null;
  const key = variations.length ? (activeVid ? `${item.id}|${activeVid}` : null) : item.id;
  const ready = variation ? variation.ready : item.ready;
  const qty = key ? cart[key] || 0 : 0;
  const price =
    item.price_source === "ESPECIFICO"
      ? item.consign_price
      : priceType === "PUBLICO"
      ? item.public_price
      : priceType === "LOJISTA"
      ? item.reseller_price
      : item.consign_price;

  const shots = [
    item.photo_path,
    ...variations.map((v) => v.photo_path),
  ].filter(Boolean);
  const current = variation?.photo_path || shots[shot] || item.photo_path;

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={item.name}
      description={variation ? variation.name : "Escolha a combinação e a quantidade"}
      testid="replenish-preview-modal"
    >
      <div className="space-y-4">
        <div className="overflow-hidden rounded-xl border border-white/10 bg-secondary/40">
          <img
            src={fileUrl(current)}
            alt={item.name}
            data-testid="preview-photo"
            className="aspect-square w-full object-cover"
          />
        </div>

        {!variation && shots.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="preview-gallery">
            {shots.map((s, i) => (
              <button
                key={s}
                type="button"
                onClick={() => setShot(i)}
                data-testid={`preview-shot-${i}`}
                className={`overflow-hidden rounded-lg border ${
                  i === shot ? "border-brand" : "border-white/10"
                }`}
              >
                <img src={fileUrl(s)} alt="" className="h-12 w-12 object-cover" />
              </button>
            ))}
          </div>
        )}

        <div>
          <p className="font-num text-xs text-muted-foreground" data-testid="preview-sku">
            {variation?.sku || item.code || "Sem código"}
            {item.kind === "KIT" ? " · kit" : ""}
          </p>
          <p className="mt-1 text-sm">
            <span className={ready ? "text-emerald-300" : "text-amber-300"}>
              {variations.length > 0 && !variation
                ? "Escolha a combinação"
                : ready
                ? "Disponível"
                : "Sob produção"}
            </span>
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2" data-testid="preview-prices">
          {[
            ["Público", item.public_price],
            ["Consignado", item.consign_missing ? null : item.consign_price],
            ["Lojista", item.reseller_price],
          ].map(([label, value]) => (
            <div key={label} className="rounded-lg border border-white/10 bg-secondary/40 p-2.5">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
              <p className="font-num text-sm">{value === null ? "não definido" : brl(value)}</p>
            </div>
          ))}
        </div>

        {variations.length > 0 && (
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Combinações
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="preview-variations">
              {variations.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() =>
                    setPicked((p) => ({ ...p, [item.id]: p[item.id] === v.id ? "" : v.id }))
                  }
                  data-testid={`preview-variation-${v.id}`}
                  className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                    v.id === activeVid
                      ? "border-brand bg-brand/15 text-brand"
                      : "border-white/15 text-muted-foreground"
                  }`}
                >
                  {v.name}
                  {cart[`${item.id}|${v.id}`] ? ` (${cart[`${item.id}|${v.id}`]})` : ""}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-white/10 pt-4">
          <span className="font-num text-sm text-muted-foreground">{brl(price)} / un</span>
          <div className="flex items-center gap-1.5 rounded-full border border-white/10 bg-background/60 px-1.5 py-1">
            <button
              type="button"
              onClick={() => change(key, -1)}
              data-testid="preview-dec"
              className="p-1.5 text-muted-foreground"
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="w-8 text-center font-num text-sm" data-testid="preview-qty">
              {qty}
            </span>
            <button
              type="button"
              onClick={() => change(key, 1, item)}
              data-testid="preview-inc"
              className="p-1.5 text-brand"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>

        <Button
          onClick={onClose}
          data-testid="preview-confirm-btn"
          className="h-11 w-full rounded-full font-semibold"
        >
          {qty > 0 ? `${qty} un na reposição · Concluir` : "Fechar"}
        </Button>
      </div>
    </ResponsiveModal>
  );
}

function RequestCard({ request: r, onReload }) {
  const [busy, setBusy] = useState(false);

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/partner/replenishments/${r.id}/cancel`);
      toast.success("Solicitação cancelada.");
      onReload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openWhatsapp = async () => {
    try {
      const { data } = await api.get(`/partner/replenishments/${r.id}/whatsapp`);
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Surface className="min-w-0 overflow-hidden p-4" data-testid={`partner-replenishment-card-${r.id}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="font-num text-sm" data-testid={`replenishment-number-${r.id}`}>
          {r.number || "—"}
        </span>
        <span
          className="shrink-0 rounded-full border border-white/15 bg-secondary/60 px-2.5 py-1 text-xs"
          data-testid={`partner-replenishment-status-${r.id}`}
        >
          {STATUS_LABEL[r.status] || r.status}
        </span>
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">{dataBR(r.created_at)}</p>
      <ul className="mt-2 space-y-1 text-sm">
        {r.items.map((it, idx) => (
          <li key={`${it.product_id}-${it.variation_id || idx}`}>
            <span className="truncate">
              <span className="font-num">{it.quantity}x</span> {it.name}
              {it.from_kit && (
                <span className="ml-1 text-[10px] text-muted-foreground">({it.from_kit})</span>
              )}
            </span>
            <ItemFulfillmentLine
              item={it}
              testid={`partner-replenishment-fulfillment-${r.id}-${it.product_id}`}
            />
          </li>
        ))}
      </ul>
      {r.fulfillment?.analyzed && (
        <p
          className="mt-2 rounded-lg border border-white/10 bg-secondary/40 px-3 py-2 text-[11px]"
          data-testid={`partner-replenishment-fulfillment-summary-${r.id}`}
        >
          {fulfillmentText(r)} · definido pela TR3ND conforme a disponibilidade.
        </p>
      )}
      <ReplenishTimeline status={r.status} />
      <div className="mt-3 flex flex-wrap gap-2 border-t border-white/10 pt-3">
        <Button
          size="sm"
          variant="outline"
          onClick={openWhatsapp}
          data-testid={`replenishment-whatsapp-btn-${r.id}`}
          className="h-9 rounded-full text-xs"
        >
          Confirmar pelo WhatsApp
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={async () => {
            try {
              await openPdf(`/partner/replenishments/${r.id}/pdf`);
            } catch (e) {
              toast.error(apiError(e));
            }
          }}
          data-testid={`partner-replenishment-pdf-btn-${r.id}`}
          className="h-9 gap-1.5 rounded-full text-xs"
        >
          <FileText className="h-3.5 w-3.5" /> Gerar PDF
        </Button>
        {r.status === "SOLICITADA" && (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={cancel}
            data-testid={`cancel-replenishment-btn-${r.id}`}
            className="h-9 rounded-full text-xs"
          >
            Cancelar
          </Button>
        )}
      </div>
    </Surface>
  );
}

function CreatedModal({ request, onClose }) {
  const wpp = request.whatsapp || {};
  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Pedido criado com sucesso"
      description={`Solicitação ${request.number} registrada. Agora avise a TR3ND.`}
      testid="replenishment-created-modal"
    >
      <div className="mt-2 space-y-4">
        <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-200">
          <Check className="h-4 w-4 shrink-0" />
          <span>Seu pedido já está no sistema. O WhatsApp é só para avisar a equipe.</span>
        </div>
        <pre className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg border border-white/10 bg-secondary/40 p-3 text-xs text-muted-foreground" data-testid="created-whatsapp-message">
          {wpp.message}
        </pre>
        <Button
          onClick={() => window.open(wpp.url, "_blank")}
          data-testid="created-whatsapp-btn"
          className="h-11 w-full rounded-full font-semibold"
        >
          Confirmar pelo WhatsApp
        </Button>
        {wpp.configured === false && (
          <p className="text-[11px] text-amber-300">
            A TR3ND ainda não cadastrou o WhatsApp. Você poderá escolher o contato na hora de
            enviar.
          </p>
        )}
      </div>
    </ResponsiveModal>
  );
}
